/**
 * React context carrying the effective appearance theme to node and edge
 * components.
 *
 * The theme is distributed via context instead of node `data` because
 * threading it through every node would churn the referential equality
 * that merge-styles.ts preserves to avoid unnecessary re-renders. A theme
 * change re-renders every consumer once — acceptable for a user-initiated
 * toggle. Consumers must not be wrapped in React.memo in a way that blocks
 * the context update.
 */
import { createContext, useContext } from "react";
import type { DiagramAppearance } from "./types";

/** The resolved theme after applying override precedence. */
type EffectiveTheme = NonNullable<DiagramAppearance["theme"]>;

const AppearanceContext = createContext<EffectiveTheme>("default");

/**
 * Resolve the effective theme from the renderer prop override and the
 * spec-provided appearance. Precedence: prop > spec > "default".
 */
export function resolveEffectiveTheme(
  propAppearance: DiagramAppearance | undefined,
  specAppearance: DiagramAppearance | undefined,
): EffectiveTheme {
  return propAppearance?.theme ?? specAppearance?.theme ?? "default";
}

/**
 * Provides the effective theme to the diagram component tree. Mounted by
 * DiagramRenderer around the rendered diagram.
 */
export function AppearanceProvider({
  theme,
  children,
}: {
  readonly theme: EffectiveTheme;
  readonly children: React.ReactNode;
}) {
  return (
    <AppearanceContext.Provider value={theme}>
      {children}
    </AppearanceContext.Provider>
  );
}

/**
 * Read the effective appearance theme. Returns "default" when rendered
 * outside an AppearanceProvider (e.g. components tested in isolation).
 */
export function useAppearanceTheme(): EffectiveTheme {
  return useContext(AppearanceContext);
}
